/**
 * Phase 8 T-20 §24 — Calendly-style booking flow modal.
 *
 * 720 × 480 modal with:
 *   Left rail  — 3 steps (Booking Link / Availability / Event Settings)
 *   Right area — step content
 *   Footer     — [Back] + [Save & continue →] (emerald)
 *
 * Config persisted to localStorage under `ggj.booking.config`.
 * Unsaved-changes guard on close (native confirm).
 * Esc closes after confirming discard.
 */

import { createPortal } from 'react-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Link as LinkIcon, Clock, Settings } from 'lucide-react';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { BookingLinkStep } from './BookingLinkStep';
import { AvailabilityStep } from './AvailabilityStep';
import { EventSettingsStep } from './EventSettingsStep';

// ── Config type & persistence ─────────────────────────────────────────────────

export interface BookingConfig {
  handle: string;
  slug: string;
  // Availability
  availStart: string;   // HH:MM (24h storage, 12h display)
  availEnd: string;     // HH:MM
  bufferBefore: number; // minutes
  bufferAfter: number;  // minutes
  advanceDays: number;  // days
  // Event settings
  durationMin: 15 | 30 | 45 | 60;
  location: 'google-meet' | 'zoom' | 'custom';
  customLocationUrl?: string;
  requirePhone: boolean;
  requireNotes: boolean;
  confirmationMessage: string;
}

const LS_KEY = 'ggj.booking.config';

const DEFAULT_CONFIG: BookingConfig = {
  handle: 'recruiter',
  slug: 'interview',
  availStart: '09:00',
  availEnd: '17:00',
  bufferBefore: 0,
  bufferAfter: 0,
  advanceDays: 14,
  durationMin: 30,
  location: 'google-meet',
  requirePhone: false,
  requireNotes: false,
  confirmationMessage:
    "Thanks for booking! We'll be in touch with further details shortly.",
};

function loadConfig(): BookingConfig {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULT_CONFIG, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_CONFIG };
}

function saveConfig(cfg: BookingConfig): void {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(cfg));
  } catch {
    /* ignore */
  }
}

// ── Steps definition ──────────────────────────────────────────────────────────

type Step = 0 | 1 | 2;

const STEPS: { label: string; icon: React.ElementType }[] = [
  { label: 'Booking Link', icon: LinkIcon },
  { label: 'Availability', icon: Clock },
  { label: 'Event Settings', icon: Settings },
];

// ── Deep-equality check (fast for our flat/shallow config) ────────────────────

function configsEqual(a: BookingConfig, b: BookingConfig): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// ── BookingFlow ───────────────────────────────────────────────────────────────

interface BookingFlowProps {
  open: boolean;
  onClose: () => void;
}

export function BookingFlow({ open, onClose }: BookingFlowProps) {
  const [step, setStep] = useState<Step>(0);
  const [saved, setSaved] = useState<BookingConfig>(() => loadConfig());
  const [draft, setDraft] = useState<BookingConfig>(() => loadConfig());
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const hasUnsaved = !configsEqual(draft, saved);

  // Reset to step 0 when modal opens
  useEffect(() => {
    if (open) {
      const loaded = loadConfig();
      setSaved(loaded);
      setDraft({ ...loaded });
      setStep(0);
    }
  }, [open]);

  const requestClose = useCallback(() => {
    if (hasUnsaved) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  }, [hasUnsaved, onClose]);

  // Esc key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, requestClose]);

  const handleChange = useCallback((patch: Partial<BookingConfig>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleSaveAndContinue = useCallback(() => {
    saveConfig(draft);
    setSaved({ ...draft });
    if (step < 2) {
      setStep((s) => (s + 1) as Step);
    } else {
      onClose();
    }
  }, [draft, step, onClose]);

  const handleBack = useCallback(() => {
    if (step > 0) setStep((s) => (s - 1) as Step);
  }, [step]);

  if (!open) return null;

  return createPortal(
    <>
      <ConfirmDialog
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        title="Discard changes?"
        message="Discard unsaved changes?"
        confirmLabel="Discard"
        variant="danger"
      />
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-[1px]"
        aria-hidden
        onClick={requestClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Booking flow settings"
        className="fixed left-1/2 top-1/2 z-[201] flex -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border shadow-2xl vscode-border vscode-bg-elevated"
        style={{
          width: 'min(720px, calc(100vw - 2rem))',
          height: 'min(480px, calc(100vh - 4rem))',
        }}
      >
        {/* ── Left rail ── */}
        <div
          className="flex w-[180px] shrink-0 flex-col border-r vscode-border"
          style={{ backgroundColor: 'var(--vscode-bg-secondary,#f9fafb)' }}
        >
          {/* Rail header */}
          <div className="border-b px-4 py-3 vscode-border">
            <p className="text-[12px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary">
              Setup
            </p>
          </div>

          {/* Steps */}
          <nav className="flex flex-1 flex-col gap-0.5 px-2 py-3" aria-label="Booking setup steps">
            {STEPS.map(({ label, icon: Icon }, idx) => {
              const isActive = step === idx;
              const isDone = idx < step;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    if (idx < step) setStep(idx as Step);
                    // Only navigate back; forward requires Save & continue
                  }}
                  disabled={idx > step}
                  className={[
                    'flex w-full items-center gap-2 rounded-[8px] px-2 py-2 text-left text-[13px] transition-colors',
                    isActive
                      ? 'bg-[#10B981]/10 font-semibold text-[#10B981]'
                      : isDone
                        ? 'vscode-text-secondary hover:vscode-bg-hover'
                        : 'vscode-text-tertiary',
                  ].join(' ')}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <Icon
                    className={`h-4 w-4 shrink-0 ${isActive ? 'text-[#10B981]' : isDone ? 'vscode-text-secondary' : 'vscode-text-tertiary'}`}
                    aria-hidden
                  />
                  <span className="truncate">{label}</span>
                  {isDone && (
                    <span
                      className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-[#10B981]"
                      aria-label="completed"
                    />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Step counter at bottom */}
          <div className="border-t px-4 py-3 vscode-border">
            <p className="text-[11px] vscode-text-tertiary">
              Step {step + 1} of {STEPS.length}
            </p>
          </div>
        </div>

        {/* ── Right content ── */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Content header */}
          <div className="flex shrink-0 items-center justify-between border-b px-5 py-3 vscode-border">
            <h2 className="text-[14px] font-semibold vscode-text-primary">
              {STEPS[step]!.label}
            </h2>
            <button
              type="button"
              onClick={requestClose}
              className="rounded-lg p-1.5 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
              aria-label="Close booking flow"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Scrollable step content */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {step === 0 && (
              <BookingLinkStep config={draft} onChange={handleChange} />
            )}
            {step === 1 && (
              <AvailabilityStep config={draft} onChange={handleChange} />
            )}
            {step === 2 && (
              <EventSettingsStep config={draft} onChange={handleChange} />
            )}
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-between border-t px-5 py-3 vscode-border">
            <div>
              {step > 0 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="rounded-[8px] px-4 py-1.5 text-[13px] font-medium vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                >
                  ← Back
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={handleSaveAndContinue}
              className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#10B981] px-4 py-1.5 text-[13px] font-medium text-white transition-opacity hover:opacity-90"
            >
              {step < 2 ? 'Save & continue →' : 'Save & finish ✓'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
